/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import redirect from 'js/lib/coursera.redirect';
import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import { Button, Typography, Typography2 } from '@coursera/cds-core';
import { SpinnerIcon } from '@coursera/cds-icons';

import { IdTypes } from 'bundles/product-features';
import { useProductFeatures } from 'bundles/program-home/productFeatureFlags';
import SsoButton from 'bundles/user-account/components/SsoButton';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

type Props = {
  programId: string;
  programName: string;
  onBrowse: (callback: () => void) => void;
  thirdPartyOrganizationId: string;
};

const styles = {
  loader: css`
    margin: var(--cds-spacing-800) 0;
  `,
  text: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  customMessage: css`
    white-space: pre-wrap;
  `,
  buttons: css`
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    gap: var(--cds-spacing-200);
    margin-top: var(--cds-spacing-200);
    align-self: stretch;

    > .c-SsoButton {
      flex: 0 1 auto;
      max-width: 320px;
    }
  `,
  ssoButton: css`
    flex: 0 1 auto;
  `,
};

const ProgramDenyModal = ({ programName, programId, onBrowse, thirdPartyOrganizationId }: Props) => {
  const { features: programFeatures, loading: isProgramProductFeaturesLoading } = useProductFeatures({
    idType: IdTypes.Program,
    id: programId,
    skip: !programId,
    ssr: true,
  });

  const router = useRouter();
  const { authProvider: ssoProvider } = router.location.query;

  const handleBrowse = React.useCallback<() => $TSFixMe>(() => {
    onBrowse(() => redirect.setLocation('/browse'));
  }, [onBrowse]);

  const isWhitelistOrganizationProgramsForSSOLogin = programFeatures.get(
    'enterprise',
    'whitelistOrganizationProgramsForSSOLogin'
  );
  const customMessage = programFeatures.get('enterprise', 'programDenyCustomMessage');

  return (
    <>
      {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
      <Typography variant="h1" component="h2" css={styles.text}>
        {_t('Hi, #{email}', { email: user.get().email_address })}
      </Typography>
      {isProgramProductFeaturesLoading ? (
        <SpinnerIcon aria-busy title={_t('Loading...')} />
      ) : (
        <>
          {customMessage ? (
            <Typography2 component="p" variant="bodyPrimary" css={[styles.text, styles.customMessage]}>
              {customMessage}
            </Typography2>
          ) : (
            <>
              <Typography2 component="p" variant="bodyPrimary" css={styles.text}>
                {_t("It looks like you haven't joined #{programName}.", { programName })}
              </Typography2>
              <Typography2 component="p" variant="bodyPrimary" css={styles.text}>
                <FormattedMessage
                  message={_t(
                    "If you believe this is an error, confirm you are signed in with using the same email you used to join the program. For more information, visit our {helpCenterLink} or contact your program's administrator."
                  )}
                  helpCenterLink={<a href="http://learner.coursera.help">{_t('Help Center')}</a>}
                />
              </Typography2>
            </>
          )}
          <div css={styles.buttons}>
            {ssoProvider && isWhitelistOrganizationProgramsForSSOLogin && <SsoButton provider={ssoProvider} />}
            <Button variant="primary" onClick={handleBrowse} data-e2e="browse-button" css={styles.ssoButton}>
              {_t('Browse Coursera')}
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default ProgramDenyModal;
